import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Card, CardContent, Typography, Box } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowUp, faArrowDown, faMinus } from '@fortawesome/free-solid-svg-icons'
import { gsap } from 'gsap'
import styles from './StatCard.module.scss'

const trendIcons = {
  up: faArrowUp,
  down: faArrowDown,
  flat: faMinus
}

const trendColors = {
  up: 'var(--color-success)',
  down: 'var(--color-error)',
  flat: 'var(--color-text-secondary)'
}

const StatCard = ({
  value,
  label,
  trend,
  icon,
  description,
  animate = true,
  decimals = 0,
  isPercentage = false,
  isRawText = false,
  size = 'medium',
  loading = false
}) => {
  const valueRef = useRef(null)

  /* istanbul ignore next */
  useEffect(() => {
    let animation

    if (animate && !isRawText && valueRef.current && value !== undefined && value !== null) {
      try {
        animation = gsap.fromTo(
          valueRef.current,
          { innerText: 0 },
          {
            innerText: parseFloat(value),
            duration: 1.5,
            ease: 'power2.out',
            snap: { innerText: 1 / Math.pow(10, decimals) },
            onUpdate () {
              if (valueRef.current) {
                let current = parseFloat(this.targets()[0].innerText)
                if (isNaN(current)) current = 0
                const formatted = decimals > 0
                  ? current.toFixed(decimals)
                  : Math.round(current)
                valueRef.current.innerText = isPercentage
                  ? `${formatted}%`
                  : formatted
              }
            }
          }
        )
      } catch (e) {
      }
    }

    return () => {
      if (animation) animation.kill()
    }
  }, [value, decimals, isPercentage, isRawText, animate])

  const formatStaticValue = () => {
    if (isRawText) return value || '\u2014'
    const num = value ?? 0
    const formatted = decimals > 0 ? num.toFixed(decimals) : num
    return isPercentage ? `${formatted}%` : formatted
  }

  return (
    <Card className={`${styles.card} ${styles[size]} ${loading ? styles.loading : ''} card-interactive`}>
      <CardContent className={styles.content}>
        <Box className={styles.header}>
          <Typography variant='body2' className={styles.label}>
            {label}
          </Typography>
          {icon && (
            <Box className={styles.iconWrapper}>
              {typeof icon === 'object' && icon.iconName
                ? <FontAwesomeIcon icon={icon} />
                : icon}
            </Box>
          )}
        </Box>

        <Typography
          variant={size === 'small' ? 'h5' : 'h4'}
          className={styles.value}
          ref={valueRef}
        >
          {formatStaticValue()}
        </Typography>

        {(trend || description) && (
          <Box className={styles.footer}>
            {trend && (
              <Box
                className={styles.trend}
                style={{ color: trendColors[trend.direction] || trendColors.flat }}
              >
                <FontAwesomeIcon
                  icon={trendIcons[trend.direction] || trendIcons.flat}
                  className={styles.trendIcon}
                  aria-hidden='true'
                />
                <Typography variant='caption' className={styles.trendValue}>
                  {trend.value != null ? `${trend.value}%` : ''}
                </Typography>
                {trend.label && (
                  <Typography variant='caption' className={styles.trendLabel}>
                    {trend.label}
                  </Typography>
                )}
              </Box>
            )}
            {description && (
              <Typography variant='caption' className={styles.description}>
                {description}
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

StatCard.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  label: PropTypes.string.isRequired,
  trend: PropTypes.shape({
    value: PropTypes.number,
    direction: PropTypes.oneOf(['up', 'down', 'flat']),
    label: PropTypes.string
  }),
  icon: PropTypes.oneOfType([PropTypes.object, PropTypes.node]),
  description: PropTypes.string,
  animate: PropTypes.bool,
  decimals: PropTypes.number,
  isPercentage: PropTypes.bool,
  isRawText: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium']),
  loading: PropTypes.bool
}

export default StatCard
